import type { CriticalAttentionFlagDetails } from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import { Button, cn } from '@seen/ui'
import { IconAlertTriangle, IconPencil } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { formatCriticalAttentionSetOn } from '#src/components/critical-attention/criticalAttentionFlag.ts'
import { ParticipantFlagBadge } from '#src/components/ParticipantFlagBadge.tsx'

// The `dark:` copies keep the pill solid in dark mode,
// where the red Badge color's own dark classes would otherwise win.
const CRITICAL_ATTENTION_PILL_CLASS_NAME =
  'border-transparent bg-badge-v2-red-solid font-semibold text-white dark:border-transparent dark:bg-badge-v2-red-solid dark:text-white'

export function CriticalAttentionFlagHoverCard({
  details,
  onEdit,
  size,
  className,
  open,
}: {
  details?: CriticalAttentionFlagDetails
  onEdit?: () => void
  size?: 'sm'
  className?: string
  open?: boolean
}): ReactNode {
  const flagDef =
    patients.PARTICIPANT_FLAGS[patients.ParticipantFlagCode.CRITICAL_ATTENTION]
  const badge = (
    <ParticipantFlagBadge
      flag={flagDef}
      leadingIcon={<IconAlertTriangle className="size-3" />}
      size={size}
      className={cn(CRITICAL_ATTENTION_PILL_CLASS_NAME, 'gap-[3px]', className)}
      disableTooltip
    />
  )

  // Always wrap the pill. Search Flags can omit code.text, which used to
  // skip the hover card entirely. Omit `open` when unset so Radix stays
  // uncontrolled. max-h-none / z-100 keep the card painted above the
  // sticky facesheet header instead of clamping to a 0-height sliver.
  return (
    <HoverCard
      openDelay={100}
      closeDelay={100}
      {...(open === undefined ? {} : { open })}
    >
      <HoverCardTrigger asChild>
        <span className="inline-flex items-center">{badge}</span>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        className="z-100 max-h-none w-72 p-4"
      >
        <CriticalAttentionFlagDetailsCard details={details} onEdit={onEdit} />
      </HoverCardContent>
    </HoverCard>
  )
}

export function CriticalAttentionFlagDetailsCard({
  details,
  onEdit,
}: {
  details?: CriticalAttentionFlagDetails
  onEdit?: () => void
}): ReactNode {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-6 items-center gap-1">
        <p className="flex-1 text-sm font-semibold">Critical Attention</p>
        {onEdit && (
          <Button
            type="button"
            variant="ghost"
            size="unstyled"
            className="size-6 shrink-0 text-muted-foreground"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              onEdit()
            }}
            aria-label="Edit Critical Attention"
          >
            <IconPencil className="size-3" />
          </Button>
        )}
      </div>
      {details ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-1 text-xs">
            <span>Set on</span>
            <span className="text-right text-muted-foreground">
              {formatCriticalAttentionSetOn(details.setOn)}
            </span>
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-center justify-between gap-1 text-xs">
            <span>Reason</span>
            <span className="text-right text-muted-foreground">
              {patients.formatCriticalAttentionReasonDisplay(details.reason)}
            </span>
          </div>
          {details.notes ? (
            <>
              <div className="h-px bg-border" />
              <div className="flex flex-col gap-1 text-xs">
                <span>Notes</span>
                <span className="text-muted-foreground">{details.notes}</span>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
